/* eslint-disable no-undef */
// Polyfill for structuredClone in environments that don't support it (Node.js < 17)
const deepClone =
  typeof structuredClone !== 'undefined'
    ? structuredClone
    : (obj) => JSON.parse(JSON.stringify(obj));
/* eslint-enable no-undef */

/**
 * Creates a legacy token set by deep cloning base tokens and overriding
 * the typography.font.fallback value with the legacy font stack.
 *
 * This approach avoids duplicating ~9000 lines per theme file while
 * maintaining proper token structure and references.
 *
 * @param {Object} baseTokens - The base token object to extend
 * @param {string} [legacyFallback='Proxima Nova, Noto Sans, Arial, sans-serif'] - The legacy font fallback string
 * @returns {Object} A new token object with legacy font fallback
 */
export function createLegacyTokens(
  baseTokens,
  legacyFallback = 'Proxima Nova, Noto Sans, Arial, sans-serif'
) {
  // Deep clone to avoid mutating the original tokens
  const legacyTokens = deepClone(baseTokens);

  // Override only the font fallback value
  if (legacyTokens.typography?.font) {
    legacyTokens.typography.font.fallback = legacyFallback;
  }

  // Update metadata to reflect this is a legacy variant
  if (legacyTokens.IDSmetadata) {
    const originalThemeName = legacyTokens.IDSmetadata.themeName;
    legacyTokens.IDSmetadata.themeName = originalThemeName
      ? `'${originalThemeName.replace(/'/g, '')} Legacy'`
      : "'Legacy'";
    legacyTokens.IDSmetadata.typographySetName = "'functionalTypography-Legacy'";
  }

  return legacyTokens;
}

/**
 * Helper to batch create multiple legacy token variants
 *
 * @param {Object} tokenMap - Map of theme names to base token objects
 * @returns {Object} Map of theme names to legacy token objects
 */
export function createLegacyTokensMap(tokenMap) {
  const legacyMap = {};

  for (const [themeName, baseTokens] of Object.entries(tokenMap)) {
    legacyMap[`${themeName}Legacy`] = createLegacyTokens(baseTokens);
  }

  return legacyMap;
}

export default createLegacyTokens;
